import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

export interface Author {
  id: string;
  name: string;
  avatar: string;
  location: string;
  role?: string | null;
}

export interface Comment {
  id: string;
  discussionId: string;
  content: string;
  author: Author;
  date: string;
  moderationStatus?: 'visible' | 'hidden' | 'removed';
  moderationReason?: string | null;
}

export interface DiscussionMedia {
  id: string;
  url: string;
  storagePath: string;
  mediaKind: 'image' | 'video';
  mimeType?: string | null;
  caption?: string | null;
  sortOrder: number;
}

export interface Discussion {
  id: string;
  title: string;
  content: string;
  author: Author;
  date: string;
  tags: string[];
  likes: number;
  comments: number;
  views: number;
  isLiked?: boolean; // Optimistic UI state
  moderationStatus?: 'visible' | 'hidden' | 'removed';
  moderationReason?: string | null;
  postType?: 'text' | 'photo' | 'video' | 'photo_carousel' | 'farm_update';
  farmId?: string | null;
  media?: DiscussionMedia[];
}

interface CommunityContextType {
  discussions: Discussion[];
  savedPosts: string[]; // IDs of saved posts
  addDiscussion: (discussion: Omit<Discussion, 'id' | 'date' | 'likes' | 'comments' | 'views' | 'author' | 'isLiked'>) => Promise<void>;
  updateDiscussion: (
    discussionId: string,
    updates: Pick<Discussion, 'title' | 'content' | 'tags' | 'postType' | 'farmId' | 'media'>,
  ) => Promise<void>;
  deleteDiscussion: (discussionId: string) => Promise<void>;
  toggleSavePost: (postId: string) => Promise<void>;
  toggleLikePost: (postId: string) => Promise<void>;
  getDiscussionById: (id: string) => Discussion | undefined;
  getComments: (discussionId: string) => Promise<Comment[]>;
  addComment: (discussionId: string, content: string) => Promise<void>;
  updateComment: (commentId: string, content: string) => Promise<void>;
  deleteComment: (commentId: string, discussionId: string) => Promise<void>;
  loading: boolean;
  refreshDiscussions: () => Promise<void>;
}

const CommunityContext = createContext<CommunityContextType | undefined>(undefined);

export const useCommunity = () => {
  const context = useContext(CommunityContext);
  if (!context) {
    throw new Error('useCommunity must be used within a CommunityProvider');
  }
  return context;
};

export const CommunityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [savedPosts, setSavedPosts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const mapMediaRows = async (rows: any[] | null | undefined): Promise<DiscussionMedia[]> => {
    const items = ((rows as any[]) || []).map((row, index) => ({
      id: String(row?.id || index),
      bucketId: String(row?.bucket_id || 'community-media'),
      storagePath: String(row?.storage_path || ''),
      mediaKind: row?.media_kind === 'video' ? ('video' as const) : ('image' as const),
      mimeType: row?.mime_type || null,
      caption: row?.caption || null,
      sortOrder: Number(row?.sort_order || index),
    }));

    const signed = await Promise.all(
      items.map(async (item) => {
        if (!item.storagePath) {
          return { ...item, url: '' };
        }

        const { data } = await supabase.storage
          .from(item.bucketId)
          .createSignedUrl(item.storagePath, 60 * 60);

        return { ...item, url: data?.signedUrl || '' };
      }),
    );

    return signed
      .map((item) => ({
        id: item.id,
        url: item.url,
        storagePath: item.storagePath,
        mediaKind: item.mediaKind,
        mimeType: item.mimeType,
        caption: item.caption,
        sortOrder: item.sortOrder,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  };

  const isCommunityEnhancementMissingError = (error: any) =>
    ['42P01', '42703'].includes(error?.code || '');

  const fetchDiscussions = async () => {
    try {
      setLoading(true);
      // Fetch discussions with author details
      let discussionsResult = await supabase
        .from('discussions')
        .select(`
          *,
          author:profiles!discussions_author_id_fkey(id, name, county, role),
          likes:discussion_likes(count),
          comments:discussion_comments(count),
          media:community_post_media(id, bucket_id, storage_path, media_kind, mime_type, caption, sort_order)
        `)
        .order('created_at', { ascending: false });

      if (discussionsResult.error && isCommunityEnhancementMissingError(discussionsResult.error)) {
        discussionsResult = await supabase
          .from('discussions')
          .select(`
            *,
            author:profiles!discussions_author_id_fkey(id, name, county, role),
            likes:discussion_likes(count),
            comments:discussion_comments(count)
          `)
          .order('created_at', { ascending: false });
      }

      const { data, error } = discussionsResult;

      if (error) throw error;

      if (data) {
        // Fetch user's likes and saved posts to set initial state
        let userLikes: string[] = [];
        let userSaved: string[] = [];

        if (user) {
          const [likesResult, savedResult] = await Promise.all([
            supabase.from('discussion_likes').select('discussion_id').eq('user_id', user.id),
            supabase.from('saved_discussions').select('discussion_id').eq('user_id', user.id)
          ]);

          if (likesResult.data) userLikes = likesResult.data.map(l => l.discussion_id);
          if (savedResult.data) userSaved = savedResult.data.map(s => s.discussion_id);
          setSavedPosts(userSaved);
        }

        const formattedDiscussions: Discussion[] = await Promise.all(data.map(async (d: any) => ({
          id: d.id,
          title: d.title,
          content: d.content,
          author: {
            id: d.author?.id || 'unknown',
            name: d.author?.name || 'Anonymous',
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(d.author?.name || 'Anonymous')}`,
            location: d.author?.county || 'Kenya',
            role: d.author?.role || null,
          },
          date: d.created_at,
          tags: d.tags || [],
          likes: d.likes?.[0]?.count || 0,
          comments: d.comments?.[0]?.count || 0,
          views: d.views || 0,
          isLiked: userLikes.includes(d.id),
          moderationStatus: d.moderation_status || 'visible',
          moderationReason: d.moderation_reason || null,
          postType: d.post_type || 'text',
          farmId: d.farm_id || null,
          media: await mapMediaRows(d.media),
        })));
        setDiscussions(formattedDiscussions);
      }
    } catch (error) {
      console.error('Error fetching discussions:', error);
      // Fallback to local/mock if empty or error (optional, mostly for dev)
      if (discussions.length === 0) {
        // Only load mocks if we really have nothing
        // setDiscussions(mockDiscussions); 
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscussions();
  }, [user]); // Refetch when user changes (login/logout)

  const addDiscussion = async (newDiscussionData: Omit<Discussion, 'id' | 'date' | 'likes' | 'comments' | 'views' | 'author' | 'isLiked'>) => {
    if (!user) return;

    try {
      const payload = {
        title: newDiscussionData.title,
        content: newDiscussionData.content,
        tags: newDiscussionData.tags,
        author_id: user.id,
        post_type: newDiscussionData.postType || ((newDiscussionData.media?.length || 0) > 1
          ? 'photo_carousel'
          : newDiscussionData.media?.[0]?.mediaKind === 'video'
            ? 'video'
            : newDiscussionData.media?.[0]?.mediaKind === 'image'
              ? 'photo'
              : 'text'),
        farm_id: newDiscussionData.farmId || null,
      };

      const requiresEnhancement = Boolean(
        newDiscussionData.media?.length ||
        newDiscussionData.farmId ||
        (newDiscussionData.postType && newDiscussionData.postType !== 'text'),
      );

      let insertResult = await supabase
        .from('discussions')
        .insert(payload)
        .select()
        .single();

      if (insertResult.error && isCommunityEnhancementMissingError(insertResult.error) && !requiresEnhancement) {
        insertResult = await supabase
          .from('discussions')
          .insert({
            title: newDiscussionData.title,
            content: newDiscussionData.content,
            tags: newDiscussionData.tags,
            author_id: user.id,
          })
          .select()
          .single();
      }

      if (insertResult.error) throw insertResult.error;

      if (newDiscussionData.media?.length) {
        const { error: mediaError } = await supabase
          .from('community_post_media')
          .insert(
            newDiscussionData.media.map((item, index) => ({
              discussion_id: insertResult.data.id,
              uploader_id: user.id,
              bucket_id: 'community-media',
              storage_path: item.storagePath,
              media_kind: item.mediaKind,
              mime_type: item.mimeType || null,
              caption: item.caption || null,
              sort_order: index,
            })),
          );

        if (mediaError) throw mediaError;
      }

      // Optimistic update or refetch
      await fetchDiscussions();
    } catch (error) {
      console.error('Error adding discussion:', error);
      throw error;
    }
  };

  const updateDiscussion = async (
    discussionId: string,
    updates: Pick<Discussion, 'title' | 'content' | 'tags' | 'postType' | 'farmId' | 'media'>,
  ) => {
    if (!user) return;

    try {
      const requiresEnhancement = Boolean(
        updates.media?.length ||
        updates.farmId ||
        (updates.postType && updates.postType !== 'text'),
      );

      let updateResult = await supabase
        .from('discussions')
        .update({
          title: updates.title,
          content: updates.content,
          tags: updates.tags,
          post_type: updates.postType || 'text',
          farm_id: updates.farmId || null,
        })
        .eq('id', discussionId)
        .eq('author_id', user.id);

      if (updateResult.error && isCommunityEnhancementMissingError(updateResult.error) && !requiresEnhancement) {
        updateResult = await supabase
          .from('discussions')
          .update({
            title: updates.title,
            content: updates.content,
            tags: updates.tags,
          })
          .eq('id', discussionId)
          .eq('author_id', user.id);
      }

      if (updateResult.error) throw updateResult.error;

      const { error: deleteMediaError } = await supabase
        .from('community_post_media')
        .delete()
        .eq('discussion_id', discussionId)
        .eq('uploader_id', user.id);

      if (deleteMediaError && !isCommunityEnhancementMissingError(deleteMediaError)) throw deleteMediaError;

      if (updates.media?.length) {
        const { error: insertMediaError } = await supabase
          .from('community_post_media')
          .insert(
            updates.media.map((item, index) => ({
              discussion_id: discussionId,
              uploader_id: user.id,
              bucket_id: 'community-media',
              storage_path: item.storagePath,
              media_kind: item.mediaKind,
              mime_type: item.mimeType || null,
              caption: item.caption || null,
              sort_order: index,
            })),
          );

        if (insertMediaError) throw insertMediaError;
      }

      await fetchDiscussions();
    } catch (error) {
      console.error('Error updating discussion:', error);
      throw error;
    }
  };

  const deleteDiscussion = async (discussionId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('discussions')
        .delete()
        .eq('id', discussionId)
        .eq('author_id', user.id);

      if (error) throw error;
      setDiscussions((prev) => prev.filter((discussion) => discussion.id !== discussionId));
    } catch (error) {
      console.error('Error deleting discussion:', error);
      throw error;
    }
  };

  const toggleSavePost = async (postId: string) => {
    if (!user) return;

    // Optimistic update
    const isSaved = savedPosts.includes(postId);
    setSavedPosts(prev => isSaved ? prev.filter(id => id !== postId) : [...prev, postId]);

    try {
      if (isSaved) {
        await supabase.from('saved_discussions').delete().eq('discussion_id', postId).eq('user_id', user.id);
      } else {
        await supabase.from('saved_discussions').insert({ discussion_id: postId, user_id: user.id });
      }
    } catch (error) {
      console.error('Error toggling saved post:', error);
      // Revert on error
      setSavedPosts(prev => isSaved ? [...prev, postId] : prev.filter(id => id !== postId));
    }
  };

  const toggleLikePost = async (postId: string) => {
    if (!user) return;

    const discussion = discussions.find(d => d.id === postId);
    if (!discussion) return;

    const isLiked = discussion.isLiked;

    // Optimistic update
    setDiscussions(prev => prev.map(d => {
      if (d.id === postId) {
        return {
          ...d,
          likes: isLiked ? d.likes - 1 : d.likes + 1,
          isLiked: !isLiked
        };
      }
      return d;
    }));

    try {
      if (isLiked) {
        await supabase.from('discussion_likes').delete().eq('discussion_id', postId).eq('user_id', user.id);
      } else {
        await supabase.from('discussion_likes').insert({ discussion_id: postId, user_id: user.id });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert
      setDiscussions(prev => prev.map(d => {
        if (d.id === postId) {
          return {
            ...d,
            likes: isLiked ? d.likes + 1 : d.likes - 1,
            isLiked: isLiked
          };
        }
        return d;
      }));
    }
  };

  const getDiscussionById = (id: string) => {
    return discussions.find(d => d.id === id);
  };

  const getComments = async (discussionId: string): Promise<Comment[]> => {
    try {
      const { data, error } = await supabase
        .from('discussion_comments')
        .select(`
          id,
          content,
          created_at,
          author_id,
          moderation_status,
          moderation_reason
        `)
        .eq('discussion_id', discussionId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const authorIds = Array.from(new Set((data || []).map((c: any) => c.author_id).filter(Boolean)));
      const { data: profilesData } = authorIds.length > 0
        ? await supabase
            .from('profiles')
            .select('id, name, county, role')
            .in('id', authorIds)
        : { data: [] as any[] };

      const profilesById = new Map((profilesData || []).map((p: any) => [p.id, p]));

      return (data || []).map((c: any) => {
        const p = profilesById.get(c.author_id);
        const name = p?.name || 'Anonymous';
        return ({
        id: c.id,
        discussionId,
        content: c.content,
        author: {
          id: c.author_id || 'unknown',
          name,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}`,
          location: p?.county || 'Kenya',
          role: p?.role || null,
        },
        date: c.created_at,
        moderationStatus: c.moderation_status || 'visible',
        moderationReason: c.moderation_reason || null,
        });
      });
    } catch (error) {
      console.error('Error fetching comments:', error);
      return [];
    }
  };

  const addComment = async (discussionId: string, content: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('discussion_comments')
        .insert({
          discussion_id: discussionId,
          author_id: user.id,
          content
        });

      if (error) throw error;
      
      // Update local discussion comment count
      setDiscussions(prev => prev.map(d => 
        d.id === discussionId 
          ? { ...d, comments: d.comments + 1 }
          : d
      ));
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  };

  const updateComment = async (commentId: string, content: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('discussion_comments')
        .update({ content })
        .eq('id', commentId)
        .eq('author_id', user.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating comment:', error);
      throw error;
    }
  };

  const deleteComment = async (commentId: string, discussionId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('discussion_comments')
        .delete()
        .eq('id', commentId)
        .eq('author_id', user.id);

      if (error) throw error;

      setDiscussions((prev) =>
        prev.map((discussion) =>
          discussion.id === discussionId
            ? { ...discussion, comments: Math.max(0, discussion.comments - 1) }
            : discussion,
        ),
      );
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  };

  return (
    <CommunityContext.Provider value={{ 
      discussions, 
      savedPosts, 
      addDiscussion, 
      updateDiscussion,
      deleteDiscussion,
      toggleSavePost, 
      toggleLikePost,
      getComments,
      addComment,
      updateComment,
      deleteComment,
      getDiscussionById,
      loading,
      refreshDiscussions: fetchDiscussions
    }}>
      {children}
    </CommunityContext.Provider>
  );
};

